// src/modules/app-views/products/pages/Init/modals/ProductsParametersModal/tabs/WarehouseFormModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Flex, Space, Form, Input, InputNumber, Switch, Typography } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import SimpleColorPicker from 'components/SimpleComponents/SimpleColorPicker';
import translateX from 'utils/translateX';

const { Text } = Typography;

// Palette of distinct colors for root warehouses
const WAREHOUSE_COLORS = [
  '#1677ff', '#52c41a', '#faad14', '#eb2f96', '#722ed1',
  '#13c2c2', '#fa541c', '#2f54eb', '#a0d911', '#f5222d',
  '#597ef7', '#36cfc9', '#ff7a45', '#9254de', '#ffc53d',
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const WarehouseFormModal = ({ open, onCancel, onSave, initialData, parentNode, loading, mousePosition, isRootLevel, usedColors = [], nextSortOrder = 1 }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const isEditing = !!initialData;
  const isAddingChild = !!parentNode;
  // Root level = creating/editing a warehouse (no sortOrder, has color + default)
  // Child level = addressing within a warehouse (sortOrder required, no color/default)
  const isChildLevel = !isRootLevel;

  const title = isEditing
    ? isRootLevel ? translateX('edit_warehouse') : translateX('edit_location')
    : isAddingChild
      ? translateX('add_location')
      : translateX('new_warehouse');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue({
          code: initialData.code || '',
          name: initialData.name || '',
          description: initialData.description || '',
          ...(isChildLevel ? { sortOrder: initialData.sortOrder ?? nextSortOrder } : {}),
          ...(isRootLevel ? { color: initialData.color || suggestColor(usedColors) } : {}),
          ...(isRootLevel ? { isDefault: initialData.isDefault || false } : {}),
        });
      } else {
        form.resetFields();
        // Suggest defaults for new nodes
        if (isRootLevel) {
          form.setFieldsValue({ color: suggestColor(usedColors) });
        }
        if (isChildLevel) {
          form.setFieldsValue({ sortOrder: nextSortOrder });
        }
      }

      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [open, initialData, form, isRootLevel, isChildLevel, usedColors, nextSortOrder]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();

      // Normalize color value (ColorPicker returns object or string)
      let colorValue = values.color;
      if (colorValue && typeof colorValue === 'object' && colorValue.toHexString) {
        colorValue = colorValue.toHexString();
      }

      onSave({
        code: (values.code || '').trim().toUpperCase(),
        name: (values.name || '').trim(),
        description: (values.description || '').trim(),
        sortOrder: isChildLevel ? (values.sortOrder ?? nextSortOrder) : null,
        color: isRootLevel ? (colorValue || null) : null,
        parentUid: parentNode?.uid || null,
        isDefault: isRootLevel ? (values.isDefault || false) : false,
      });
    } catch (error) {
      console.log('[WarehouseFormModal] Validation failed:', error);
    }
  }, [form, onSave, parentNode, isRootLevel, isChildLevel, nextSortOrder]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  const handleAfterClose = useCallback(() => {
    form.resetFields();
  }, [form]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      afterClose={handleAfterClose}
      destroyOnHidden
      centered
      mousePosition={mousePosition}
      getContainer={document.body}
      width={480}
      footer={
        <Flex justify="end" style={{ paddingTop: 8 }}>
          <Space>
            <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
              {translateX('cancel')}
            </Button>
            <Button
              type="primary"
              loading={loading}
              icon={<CheckOutlined />}
              onClick={handleSubmit}
            >
              {translateX('save')}
            </Button>
          </Space>
        </Flex>
      }
    >
      {isAddingChild && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            {translateX('parent_location')}:{' '}
            <Text strong>
              {parentNode.code ? `[${parentNode.code}] ` : ''}
              {parentNode.name}
            </Text>
          </Text>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onKeyDown={handleKeyDown}
        initialValues={{ code: '', name: '', description: '' }}
      >
        {/* Row 1: [SortOrder (child only)] + Code + Name */}
        <Flex gap={16}>
          {isChildLevel && (
            <Form.Item
              name="sortOrder"
              label={translateX('picking_order')}
              rules={[{ required: true, message: translateX('required_field') }]}
              style={{ width: 80, flexShrink: 0 }}
            >
              <InputNumber
                min={1}
                max={9999}
                style={{ width: '100%', height: 40 }}
                disabled={loading}
              />
            </Form.Item>
          )}

          <Form.Item
            name="code"
            label={translateX('code')}
            rules={[{ required: true, message: translateX('required_field') }]}
            style={{ width: 120, flexShrink: 0 }}
          >
            <Input
              ref={inputRef}
              maxLength={20}
              style={{ height: 40, textTransform: 'uppercase' }}
              placeholder={isAddingChild ? 'R01' : 'A01'}
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label={translateX('name')}
            rules={[{ required: true, message: translateX('required_field') }]}
            style={{ flex: 1 }}
          >
            <Input
              maxLength={50}
              style={{ height: 40 }}
              placeholder={isAddingChild ? translateX('location_name_placeholder') : translateX('warehouse_name_placeholder')}
              disabled={loading}
            />
          </Form.Item>
        </Flex>

        {/* Row 2: Description + Color picker (root only) */}
        <Flex gap={16}>
          <Form.Item
            name="description"
            label={translateX('description')}
            style={{ flex: 1, marginBottom: isRootLevel ? undefined : 0 }}
          >
            <Input
              maxLength={200}
              placeholder={translateX('warehouse_description_placeholder')}
              disabled={loading}
              style={{ height: 40 }}
            />
          </Form.Item>

          {isRootLevel && (
            <Form.Item
              name="color"
              label={translateX('color')}
              style={{ flexShrink: 0 }}
            >
              <SimpleColorPicker
                size="small"
                disabled={loading}
                presets={[{ label: translateX('suggested'), colors: WAREHOUSE_COLORS }]}
              />
            </Form.Item>
          )}
        </Flex>

        {/* Default warehouse switch — only for root warehouses */}
        {isRootLevel && (
          <Flex align="center" gap={8} style={{ marginTop: 4 }}>
            <Form.Item
              name="isDefault"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch disabled={loading} />
            </Form.Item>
            <Text type="secondary">{translateX('default_warehouse')}</Text>
          </Flex>
        )}
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default WarehouseFormModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Suggest a random color not yet used by other warehouses.
 */
function suggestColor(usedColors) {
  const available = WAREHOUSE_COLORS.filter(c => !usedColors.includes(c));
  if (available.length > 0) {
    return available[Math.floor(Math.random() * available.length)];
  }
  return WAREHOUSE_COLORS[Math.floor(Math.random() * WAREHOUSE_COLORS.length)];
}
